import { useCallback } from 'react'
import dayjs from 'dayjs'
import type { DateRange } from 'react-day-picker'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { useWorkspaceTabState } from '#/shared/lib/workspace-tabs'

import type { ShipmentStage } from '../model/stages'
import { SHIPMENT_STAGES } from '../model/stages'
import { OWNER_ALL_USERS } from './owner-filter'
import type { ShipmentListFilters } from './use-shipment-list-page'

export const SHIPMENT_FILTER_KEYS = {
  FIRST_SHIP: 'firstShip',
  LAST_EDITED: 'lastEdited',
  TASK_DEADLINE: 'taskDeadline',
  CONVERTED: 'convertedDate',
  DISPATCHED: 'dispatchedDate',
  PICKED_UP: 'pickedUpDate',
  DELIVERED: 'deliveredDate',
  STAGE_DATE: 'stageDate',
} as const

export type ShipmentFilterKey =
  (typeof SHIPMENT_FILTER_KEYS)[keyof typeof SHIPMENT_FILTER_KEYS]

/**
 * Every date filter except the stage date, which is special: its URL keys and
 * API fields depend on which stage's page you are on (see `STAGE_DATE_CONFIG`).
 */
export type ShipmentDateFilterKey = Exclude<ShipmentFilterKey, 'stageDate'>

/**
 * Multi-value (chip list) filters. Unlike the date ranges these carry a
 * `string[]` and serialise to a single comma-joined URL param whose name
 * doubles as the filter key.
 */
export const SHIPMENT_MULTI_FILTER_KEYS = {
  TIME_ZONE: 'timezones',
  PICKUP_STATE: 'pickupStates',
  DELIVERY_STATE: 'deliveryStates',
  SOURCES: 'sources',
} as const

export type ShipmentMultiFilterKey =
  (typeof SHIPMENT_MULTI_FILTER_KEYS)[keyof typeof SHIPMENT_MULTI_FILTER_KEYS]

/**
 * The `ShipmentListFilters` fields that carry a date bound — every `*Gte`/`*Lte`
 * pair, and nothing else. Keeps a config from pointing a date range at, say,
 * `status`, whose value is an enum rather than a `YYYY-MM-DD` string.
 */
type ShipmentDateField = Extract<
  keyof ShipmentListFilters,
  `${string}Gte` | `${string}Lte`
>

interface StageDateConfig {
  urlPrefix: string
  labelI18nKey: string
  apiGteField: ShipmentDateField
  apiLteField: ShipmentDateField
}

const STAGE_DATE_CONFIG: Record<ShipmentStage, StageDateConfig> = {
  [SHIPMENT_STAGES.LEAD]: {
    urlPrefix: 'leadDate',
    labelI18nKey: 'toolbar.filterOptions.leadDate',
    apiGteField: 'leadDateGte',
    apiLteField: 'leadDateLte',
  },
  [SHIPMENT_STAGES.QUOTE]: {
    urlPrefix: 'quoteDate',
    labelI18nKey: 'toolbar.filterOptions.quoteDate',
    apiGteField: 'quoteDateGte',
    apiLteField: 'quoteDateLte',
  },
  [SHIPMENT_STAGES.ORDER]: {
    urlPrefix: 'orderDate',
    labelI18nKey: 'toolbar.filterOptions.orderDate',
    apiGteField: 'orderDateGte',
    apiLteField: 'orderDateLte',
  },
}

export function getStageDateConfig(stage: ShipmentStage): StageDateConfig {
  return STAGE_DATE_CONFIG[stage]
}

interface DateFilterConfig {
  labelI18nKey: string
  apiGteField: ShipmentDateField
  apiLteField: ShipmentDateField
}

/**
 * The date ranges whose URL keys and API fields are the same on every page. The
 * URL key is the filter key itself plus `From`/`To` (`?dispatchedDateFrom=…`);
 * the API wants a `__gte`/`__lte` pair, mapped in `SHIPMENT_API_KEY_BY_FILTER`.
 *
 * Which of these a page actually offers is not decided here — see FILTER_LAYOUT.
 */
const DATE_FILTER_CONFIG: Record<ShipmentDateFilterKey, DateFilterConfig> = {
  [SHIPMENT_FILTER_KEYS.FIRST_SHIP]: {
    labelI18nKey: 'toolbar.filterOptions.firstShip',
    apiGteField: 'firstAvailableDateGte',
    apiLteField: 'firstAvailableDateLte',
  },
  [SHIPMENT_FILTER_KEYS.LAST_EDITED]: {
    labelI18nKey: 'toolbar.filterOptions.lastEdited',
    apiGteField: 'lastEditedGte',
    apiLteField: 'lastEditedLte',
  },
  [SHIPMENT_FILTER_KEYS.TASK_DEADLINE]: {
    labelI18nKey: 'toolbar.filterOptions.taskDeadline',
    apiGteField: 'taskDeadlineGte',
    apiLteField: 'taskDeadlineLte',
  },
  [SHIPMENT_FILTER_KEYS.CONVERTED]: {
    labelI18nKey: 'toolbar.filterOptions.convertedDate',
    apiGteField: 'convertedGte',
    apiLteField: 'convertedLte',
  },
  [SHIPMENT_FILTER_KEYS.DISPATCHED]: {
    labelI18nKey: 'toolbar.filterOptions.dispatchedDate',
    apiGteField: 'dispatchedGte',
    apiLteField: 'dispatchedLte',
  },
  [SHIPMENT_FILTER_KEYS.PICKED_UP]: {
    labelI18nKey: 'toolbar.filterOptions.pickedUpDate',
    apiGteField: 'pickedUpGte',
    apiLteField: 'pickedUpLte',
  },
  [SHIPMENT_FILTER_KEYS.DELIVERED]: {
    labelI18nKey: 'toolbar.filterOptions.deliveredDate',
    apiGteField: 'deliveredGte',
    apiLteField: 'deliveredLte',
  },
}

export function getDateFilterConfig(
  key: ShipmentDateFilterKey,
): DateFilterConfig {
  return DATE_FILTER_CONFIG[key]
}

/** Any filter the popover can show — a date range or a multi-value list. */
export type ShipmentAnyFilterKey = ShipmentFilterKey | ShipmentMultiFilterKey

export function isDateFilterKey(
  key: ShipmentAnyFilterKey,
): key is ShipmentDateFilterKey {
  return key in DATE_FILTER_CONFIG
}

/**
 * The filters panel, per page: an ordered list of groups, each an ordered
 * list of filters. This is the single source of truth for *which* filters a
 * stage offers — a filter absent here is not shown, not sent to the API, and
 * ignored if it somehow appears in the URL.
 *
 * Orders follows the Figma spec (node 6049:248073) exactly: no order-date and no
 * sources, and the lifecycle dates lead. The design also lists a Payment filter,
 * left out until the backend has one.
 */
const FILTER_LAYOUT: Record<
  ShipmentStage,
  ReadonlyArray<readonly ShipmentAnyFilterKey[]>
> = {
  [SHIPMENT_STAGES.LEAD]: [
    [
      SHIPMENT_FILTER_KEYS.STAGE_DATE,
      SHIPMENT_FILTER_KEYS.FIRST_SHIP,
      SHIPMENT_FILTER_KEYS.LAST_EDITED,
      SHIPMENT_FILTER_KEYS.TASK_DEADLINE,
    ],
    [
      SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE,
      SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE,
      SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE,
      SHIPMENT_MULTI_FILTER_KEYS.SOURCES,
    ],
  ],
  [SHIPMENT_STAGES.QUOTE]: [
    [
      SHIPMENT_FILTER_KEYS.STAGE_DATE,
      SHIPMENT_FILTER_KEYS.FIRST_SHIP,
      SHIPMENT_FILTER_KEYS.LAST_EDITED,
      SHIPMENT_FILTER_KEYS.TASK_DEADLINE,
    ],
    [
      SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE,
      SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE,
      SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE,
      SHIPMENT_MULTI_FILTER_KEYS.SOURCES,
    ],
  ],
  [SHIPMENT_STAGES.ORDER]: [
    [
      SHIPMENT_FILTER_KEYS.FIRST_SHIP,
      SHIPMENT_FILTER_KEYS.DISPATCHED,
      SHIPMENT_FILTER_KEYS.PICKED_UP,
      SHIPMENT_FILTER_KEYS.DELIVERED,
    ],
    [
      SHIPMENT_FILTER_KEYS.LAST_EDITED,
      SHIPMENT_FILTER_KEYS.CONVERTED,
      SHIPMENT_FILTER_KEYS.TASK_DEADLINE,
    ],
    [
      SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE,
      SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE,
      SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE,
    ],
  ],
}

function getFilterKeys(stage: ShipmentStage): ShipmentAnyFilterKey[] {
  return FILTER_LAYOUT[stage].flat()
}

function getDateFilterKeys(stage: ShipmentStage): ShipmentDateFilterKey[] {
  return getFilterKeys(stage).filter(isDateFilterKey)
}

const MULTI_KEY_SET: ReadonlySet<string> = new Set(
  Object.values(SHIPMENT_MULTI_FILTER_KEYS),
)

export function isMultiFilterKey(
  key: ShipmentAnyFilterKey,
): key is ShipmentMultiFilterKey {
  return MULTI_KEY_SET.has(key)
}

function getMultiFilterKeys(stage: ShipmentStage): ShipmentMultiFilterKey[] {
  return getFilterKeys(stage).filter(isMultiFilterKey)
}

/** Stages that show the stage-date range (orders drop it, per the design). */
function hasStageDate(stage: ShipmentStage): boolean {
  return getFilterKeys(stage).includes(SHIPMENT_FILTER_KEYS.STAGE_DATE)
}

interface MultiFilterConfig {
  labelI18nKey: string
}

/**
 * Labels for the multi-value filters. The matching API fields are assembled in
 * `buildApiFilters`; note `sources` is the odd one out — the UI calls it a
 * source, the API calls it `provider_id__in`.
 */
const MULTI_FILTER_CONFIG: Record<ShipmentMultiFilterKey, MultiFilterConfig> = {
  [SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE]: {
    labelI18nKey: 'toolbar.filterOptions.timeZone',
  },
  [SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE]: {
    labelI18nKey: 'toolbar.filterOptions.pickupState',
  },
  [SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE]: {
    labelI18nKey: 'toolbar.filterOptions.deliveryState',
  },
  [SHIPMENT_MULTI_FILTER_KEYS.SOURCES]: {
    labelI18nKey: 'toolbar.filterOptions.sources',
  },
}

export function getMultiFilterConfig(
  key: ShipmentMultiFilterKey,
): MultiFilterConfig {
  return MULTI_FILTER_CONFIG[key]
}

export const SHIPMENT_MULTI_FILTER_KEY_LIST = Object.values(
  SHIPMENT_MULTI_FILTER_KEYS,
) as ShipmentMultiFilterKey[]

type RangeState = Partial<Record<ShipmentFilterKey, DateRange | undefined>>
type MultiState = Partial<Record<ShipmentMultiFilterKey, string[] | undefined>>

const API_DATE_FORMAT = 'YYYY-MM-DD'
const API_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
const MULTI_VALUE_SEPARATOR = ','

function parseUrlDate(value: string | null): Date | undefined {
  if (value === null || !API_DATE_REGEX.test(value)) return undefined
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toDate() : undefined
}

function formatApiDate(date: Date | undefined): string | undefined {
  return date ? dayjs(date).format(API_DATE_FORMAT) : undefined
}

function buildRange(
  from: string | null,
  to: string | null,
): DateRange | undefined {
  const fromDate = parseUrlDate(from)
  const toDate = parseUrlDate(to)
  if (!fromDate && !toDate) return undefined
  return { from: fromDate, to: toDate }
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

// The URL already holds the exact comma-joined string the API wants, so the
// list form is only ever needed for the UI (chips, multi-select drafts).
function parseMultiValue(value: string | null): string[] | undefined {
  if (value === null) return undefined
  const parts = value
    .split(MULTI_VALUE_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
  return parts.length > 0 ? parts : undefined
}

function formatMultiValue(values: string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined
  return values.join(MULTI_VALUE_SEPARATOR)
}

export interface ShipmentListFiltersState {
  stage: ShipmentStage
  /**
   * The panel's contents for this stage: ordered groups of ordered filter
   * keys, straight from FILTER_LAYOUT.
   */
  groups: ReadonlyArray<readonly ShipmentAnyFilterKey[]>
  /** Multi-value filters this stage offers — the chip row iterates these. */
  multiKeys: ShipmentMultiFilterKey[]
  ranges: RangeState
  setRange: (key: ShipmentFilterKey, range: DateRange | undefined) => void
  multi: MultiState
  setMulti: (key: ShipmentMultiFilterKey, values: string[] | undefined) => void
  /** Removes every filter this stage offers in a single URL write. */
  clearAll: () => void
  count: number
  isOpen: boolean
  setOpen: (open: boolean) => void
  userId: string | null
  setUserId: (userId: string | null) => void
  apiFilters: ShipmentListFilters
}

type LooseSearch = Record<string, unknown>

/** A date range as it sits in the URL: two `YYYY-MM-DD` strings, or nulls. */
interface RawRange {
  from: string | null
  to: string | null
}

function readRange(raw: LooseSearch, urlPrefix: string): RawRange {
  return {
    from: readString(raw[`${urlPrefix}From`]),
    to: readString(raw[`${urlPrefix}To`]),
  }
}

/**
 * The filters as the URL currently holds them. Only the date filters this stage
 * offers are read: a stray `?deliveredDateFrom` on the leads page is ignored
 * rather than silently applied to a query whose page has no way to clear it.
 */
function useRawFilterValues(stage: ShipmentStage) {
  const raw: LooseSearch = useSearch({ strict: false })
  const stageDateConfig = STAGE_DATE_CONFIG[stage]
  const dateKeys = getDateFilterKeys(stage)
  const multiKeys = getMultiFilterKeys(stage)

  const dates = {} as Record<ShipmentDateFilterKey, RawRange>
  for (const key of dateKeys) dates[key] = readRange(raw, key)

  const multi = {} as Partial<Record<ShipmentMultiFilterKey, string | null>>
  for (const key of multiKeys) multi[key] = readString(raw[key])

  return {
    stage,
    stageDateConfig,
    dateKeys,
    multiKeys,
    dates,
    // Orders drop the stage date entirely, so don't even read it there — an
    // `?orderDateFrom` left over from another page must not silently filter.
    stageDate: hasStageDate(stage)
      ? readRange(raw, stageDateConfig.urlPrefix)
      : { from: null, to: null },
    userIdParam: readString(raw['userId']),
    multi,
  }
}

type RawFilterValues = ReturnType<typeof useRawFilterValues>

function buildRanges(values: RawFilterValues): RangeState {
  const ranges: RangeState = {}
  if (hasStageDate(values.stage)) {
    ranges[SHIPMENT_FILTER_KEYS.STAGE_DATE] = buildRange(
      values.stageDate.from,
      values.stageDate.to,
    )
  }
  for (const key of values.dateKeys) {
    ranges[key] = buildRange(values.dates[key].from, values.dates[key].to)
  }
  return ranges
}

function buildMulti(values: RawFilterValues): MultiState {
  const multi: MultiState = {}
  for (const key of values.multiKeys) {
    multi[key] = parseMultiValue(values.multi[key] ?? null)
  }
  return multi
}

/**
 * The API field a multi-value filter maps onto — the comma-joined string ones
 * only, so a config can't point a chip list at `status` (an enum) by mistake.
 * Sources is the odd one out: the UI calls it a source, the API `provider_id__in`.
 */
type ShipmentMultiField = Extract<
  keyof ShipmentListFilters,
  'timezones' | 'pickupStates' | 'deliveryStates' | 'providerIdIn'
>

const MULTI_API_FIELD: Record<ShipmentMultiFilterKey, ShipmentMultiField> = {
  [SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE]: 'timezones',
  [SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE]: 'pickupStates',
  [SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE]: 'deliveryStates',
  [SHIPMENT_MULTI_FILTER_KEYS.SOURCES]: 'providerIdIn',
}

/**
 * The camelCase filter payload `useShipmentListPage` maps onto the API's
 * snake_case params. Only the filters this stage offers are sent — a page that
 * doesn't show Sources never filters by it, whatever the URL says.
 *
 * Not memoised: the caller rebuilds its request params every render anyway, and
 * TanStack Query keys on value rather than identity.
 */
function buildApiFilters(values: RawFilterValues): ShipmentListFilters {
  const { stageDateConfig } = values

  const filters: ShipmentListFilters = {
    userId: values.userIdParam === OWNER_ALL_USERS ? null : values.userIdParam,
  }

  if (hasStageDate(values.stage)) {
    filters[stageDateConfig.apiGteField] = values.stageDate.from
    filters[stageDateConfig.apiLteField] = values.stageDate.to
  }

  for (const key of values.dateKeys) {
    const config = DATE_FILTER_CONFIG[key]
    filters[config.apiGteField] = values.dates[key].from
    filters[config.apiLteField] = values.dates[key].to
  }

  // Already stored comma-joined in the URL — exactly the form the API takes.
  for (const key of values.multiKeys) {
    filters[MULTI_API_FIELD[key]] = values.multi[key] ?? null
  }

  return filters
}

interface FilterWriters {
  setRange: (key: ShipmentFilterKey, range: DateRange | undefined) => void
  setMulti: (key: ShipmentMultiFilterKey, values: string[] | undefined) => void
  setUserId: (userId: string | null) => void
  clearAll: () => void
}

function useFilterWriters(stage: ShipmentStage): FilterWriters {
  const stageDateConfig = STAGE_DATE_CONFIG[stage]
  const navigate = useNavigate()

  const writeFilter = useCallback(
    (patch: Record<string, string | undefined>) => {
      const searchUpdater = (prev: LooseSearch): LooseSearch => {
        const next: LooseSearch = { ...prev }
        for (const [key, value] of Object.entries(patch)) {
          if (value == null || value === '') delete next[key]
          else next[key] = value
        }
        delete next['from']
        delete next['to']
        delete next['page']
        return next
      }
      navigate({ search: searchUpdater as never, replace: true })
    },
    [navigate],
  )

  const setRange = useCallback(
    (key: ShipmentFilterKey, range: DateRange | undefined) => {
      const urlPrefix =
        key === SHIPMENT_FILTER_KEYS.STAGE_DATE
          ? stageDateConfig.urlPrefix
          : key
      writeFilter({
        [`${urlPrefix}From`]: formatApiDate(range?.from),
        [`${urlPrefix}To`]: formatApiDate(range?.to),
      })
    },
    [writeFilter, stageDateConfig.urlPrefix],
  )

  const setMulti = useCallback(
    (key: ShipmentMultiFilterKey, values: string[] | undefined) => {
      writeFilter({ [key]: formatMultiValue(values) })
    },
    [writeFilter],
  )

  const setUserId = useCallback(
    (userId: string | null) => {
      writeFilter({ userId: userId ?? undefined })
    },
    [writeFilter],
  )

  const clearAll = useCallback(() => {
    const patch: Record<string, string | undefined> = {}
    if (hasStageDate(stage)) {
      patch[`${stageDateConfig.urlPrefix}From`] = undefined
      patch[`${stageDateConfig.urlPrefix}To`] = undefined
    }
    for (const key of getDateFilterKeys(stage)) {
      patch[`${key}From`] = undefined
      patch[`${key}To`] = undefined
    }
    for (const key of getMultiFilterKeys(stage)) patch[key] = undefined
    writeFilter(patch)
  }, [writeFilter, stage, stageDateConfig.urlPrefix])

  return { setRange, setMulti, setUserId, clearAll }
}

export function useShipmentListFilters(
  stage: ShipmentStage,
): ShipmentListFiltersState {
  const [isOpen, setOpen] = useWorkspaceTabState(`filtersOpen:${stage}`, false)
  const values = useRawFilterValues(stage)
  const ranges = buildRanges(values)
  const multi = buildMulti(values)
  const apiFilters = buildApiFilters(values)
  const { setRange, setMulti, setUserId, clearAll } = useFilterWriters(stage)
  const count =
    Object.values(ranges).filter(Boolean).length +
    Object.values(multi).filter(Boolean).length

  return {
    stage,
    groups: FILTER_LAYOUT[stage],
    multiKeys: values.multiKeys,
    ranges,
    setRange,
    multi,
    setMulti,
    clearAll,
    count,
    isOpen,
    setOpen,
    userId: values.userIdParam,
    setUserId,
    apiFilters,
  }
}
